"use client";

import { Chip } from "@heroui/chip";
import { extendVariants } from "@heroui/system";

/**
 * Defines all available color options for the ColorChip component
 * Based on HeroUI's common and semantic colors
 */
export type ChipColor =
  // Semantic colors
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger"
  // Common colors
  | "white"
  | "black"
  | "blue"
  | "purple"
  | "green"
  | "red"
  | "pink"
  | "yellow"
  | "cyan"
  | "zinc";

/**
 * A Chip component extended with HeroUI's common colors and improved accessibility.
 * This uses the recommended extendVariants function for proper extension.
 *
 * Accessibility features:
 * - Adds proper role="status" for status chips (dot variant)
 * - Sets role="button" for interactive chips
 * - Supports aria-label for screen readers
 * - Provides aria-selected for selected state
 * - Includes aria-live for dynamic content
 * - Supports tabIndex for keyboard navigation
 * - Ensures adequate color contrast for readability
 * - Maintains accessible text size and spacing
 */
export const ColorChip = extendVariants(Chip, {
  compoundVariants: [
    // Black variant compounds
    {
      className: "bg-transparent text-black border-black",
      color: "black",
      variant: "bordered",
    },
    {
      className: "bg-gray-100 text-gray-900 border-transparent",
      color: "black",
      variant: "flat",
    },

    // Blue variant compounds
    {
      className: "bg-transparent text-blue-500 border-blue-500",
      color: "blue",
      variant: "bordered",
    },
    {
      className: "bg-blue-100 text-blue-700 border-transparent",
      color: "blue",
      variant: "flat",
    },
    {
      className: "bg-blue-500 text-white shadow-lg shadow-blue-500/40",
      color: "blue",
      variant: "shadow",
    },

    // Purple variant compounds
    {
      className: "bg-transparent text-purple-500 border-purple-500",
      color: "purple",
      variant: "bordered",
    },
    {
      className: "bg-purple-100 text-purple-700 border-transparent",
      color: "purple",
      variant: "flat",
    },
    {
      className: "bg-purple-500 text-white shadow-lg shadow-purple-500/40",
      color: "purple",
      variant: "shadow",
    },

    // Green variant compounds
    {
      className: "bg-transparent text-green-500 border-green-500",
      color: "green",
      variant: "bordered",
    },
    {
      className: "bg-green-100 text-green-700 border-transparent",
      color: "green",
      variant: "flat",
    },
    {
      className: "bg-green-500 text-white shadow-lg shadow-green-500/40",
      color: "green",
      variant: "shadow",
    },

    // Red variant compounds
    {
      className: "bg-transparent text-red-500 border-red-500",
      color: "red",
      variant: "bordered",
    },
    {
      className: "bg-red-100 text-red-700 border-transparent",
      color: "red",
      variant: "flat",
    },

    // Pink variant compounds
    {
      className: "bg-transparent text-pink-500 border-pink-500",
      color: "pink",
      variant: "bordered",
    },
    {
      className: "bg-pink-100 text-pink-700 border-transparent",
      color: "pink",
      variant: "flat",
    },

    // Yellow variant compounds
    {
      className: "bg-transparent text-yellow-500 border-yellow-500",
      color: "yellow",
      variant: "bordered",
    },
    {
      className: "bg-yellow-100 text-yellow-700 border-transparent", // Darkened for better contrast
      color: "yellow",
      variant: "flat",
    },

    // Cyan variant compounds
    {
      className: "bg-transparent text-cyan-500 border-cyan-500",
      color: "cyan",
      variant: "bordered",
    },
    {
      className: "bg-cyan-100 text-cyan-700 border-transparent",
      color: "cyan",
      variant: "flat",
    },

    // Zinc variant compounds
    {
      className: "bg-transparent text-zinc-500 border-zinc-500",
      color: "zinc",
      variant: "bordered",
    },
    {
      className: "bg-zinc-100 text-zinc-700 border-transparent",
      color: "zinc",
      variant: "flat",
    },

    // White variant compounds
    {
      className: "bg-transparent text-gray-900 border-gray-300",
      color: "white",
      variant: "bordered",
    },
    {
      className: "bg-gray-100 text-gray-900 border-transparent", // Darkened for better contrast
      color: "white",
      variant: "flat",
    },

    // Dot variants - handled by specific color and dot variant combinations
    {
      className: "bg-transparent text-foreground border-default",
      color: "black",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "blue",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "purple",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "green",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "red",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "pink",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "yellow",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "cyan",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "zinc",
      variant: "dot",
    },
    {
      className: "bg-transparent text-foreground border-default",
      color: "white",
      variant: "dot",
    },
    // Accessibility augmentations
    {
      className: "",
      props: {
        role: "status",
      },
      variant: "dot",
    },
    {
      className: "",
      isSelected: true,
      props: {
        "aria-selected": "true",
      },
    },
    // Keyboard interaction support
    {
      className:
        "focus:outline-2 focus:outline-offset-2 focus:outline-blue-500",
      isClickable: true,
    },
    // Additional ARIA support
    {
      className: "",
      isDismissable: true,
      props: {
        "aria-label": "Dismiss",
      },
    },
  ],
  defaultVariants: {
    color: "default",
    shade: 500,
    variant: "solid",
  },
  slots: {
    avatar: "aria-hidden='true'", // Hide avatar from screen readers when decorative
    base: "relative max-w-fit inline-flex items-center justify-between whitespace-nowrap min-h-[1.5rem] text-sm",
    closeButton: "aria-label='close' role='button' tabindex='0'", // Improved accessibility for close button
    content: "flex-1 text-foreground font-normal",
    dot: "w-2 h-2 ml-1 rounded-full",
  },
  variants: {
    color: {
      black: {
        base: "bg-black text-white border-gray-800",
        dot: "bg-black",
      },
      blue: {
        base: "bg-blue-500 text-white border-blue-600",
        dot: "bg-blue-500",
      },
      cyan: {
        base: "bg-cyan-500 text-white border-cyan-600",
        dot: "bg-cyan-500",
      },
      green: {
        base: "bg-green-500 text-white border-green-600",
        dot: "bg-green-500",
      },
      pink: {
        base: "bg-pink-500 text-white border-pink-600",
        dot: "bg-pink-500",
      },
      purple: {
        base: "bg-purple-500 text-white border-purple-600",
        dot: "bg-purple-500",
      },
      red: {
        base: "bg-red-500 text-white border-red-600",
        dot: "bg-red-500",
      },
      white: {
        base: "bg-white text-gray-800 border-gray-300", // Darkened for better contrast
        dot: "bg-gray-400",
      },
      yellow: {
        base: "bg-yellow-500 text-black border-yellow-600", // Changed to black for better contrast
        dot: "bg-yellow-500",
      },
      zinc: {
        base: "bg-zinc-500 text-white border-zinc-600",
        dot: "bg-zinc-500",
      },
    },
    shade: {
      50: {},
      100: {},
      200: {},
      300: {},
      400: {},
      500: {}, // default
      600: {},
      700: {},
      800: {},
      900: {},
    },
    variant: {
      bordered: {
        base: "bg-transparent border-medium",
      },
      dot: {
        base: "bg-transparent border-default text-foreground",
        dot: "!block !visible", // Force display the dot
      },
      flat: {
        base: "border-transparent",
      },
      shadow: {
        base: "shadow-md",
      },
      solid: {
        base: "",
      },
    },
  },
});

ColorChip.displayName = "ColorChip";
